import { generateID } from 'react-querybuilder'
import { finalize, map, switchMap } from 'rxjs'
import api from '../api'
import { errorHandler } from '../utils/errors'
import { getApiHost } from '../utils/site'
import { decrLoading, incrLoading } from '../zustand/user'
import fromFetch from './from-fetch'

const baseUrl = `${getApiHost()}/_expurgate-notifications`

let queued = []
let subscribers = []

const ws = {
  current: { send: (m) => queued.push(m) }
}

const flushQueued = () => {
  queued.forEach((m) => {
    ws.current.send(m)
  })
  queued = []
}

const getHeaders = (hasBody) => {
  const headers = {
    'accept-encoding': 'br, gzip, deflate',
    accept: 'application/json'
  }

  if (hasBody) {
    headers['content-type'] = 'application/json'
  }

  return headers
}

const messageHandler = (e) => {
  const { data, type } = JSON.parse(e.data)

  if (type === 'ERROR') {
    return errorHandler({ message: data })
  }

  subscribers.forEach((s) => {
    if (s.type === type) {
      s.callback?.(data)
    }
  })
}

export const connectWebSocket = () => {
  api.authentication.authenticate$().subscribe(() => {
    ws.current = new WebSocket(`${baseUrl.replace(/^https/, 'wss')}/ws`)
    ws.current.onclose = (e) => {
      if (!e.wasClean) {
        setTimeout(connectWebSocket, 5000)
      }
    }
    ws.current.onmessage = messageHandler
    ws.current.onopen = flushQueued
  })
}

const adapter = (endpoint = '/') => ({
  create$: (data = {}, options = {}) => {
    !options.ignoreLoadingState && incrLoading()

    const req = new Request(`${baseUrl}${endpoint}`, {
      body: JSON.stringify(data),
      headers: getHeaders(true),
      method: 'POST'
    })

    return fromFetch(req).pipe(
      switchMap((res) => res.text()),
      finalize(() => !options.ignoreLoadingState && decrLoading())
    )
  },

  disconnect: () => {
    ws.current.close?.()
  },

  find$: (options = {}) => {
    !options.ignoreLoadingState && incrLoading()

    const req = new Request(`${baseUrl}${endpoint}`, {
      headers: getHeaders()
    })

    return fromFetch(req).pipe(
      switchMap((res) => res.text()),
      map((body) => {
        if (!body.startsWith('[')) {
          throw new Error(body)
        }
        return JSON.parse(body)
      }),
      finalize(() => !options.ignoreLoadingState && decrLoading())
    )
  },

  findOne$: (id = '', options = {}) => {
    !options.ignoreLoadingState && incrLoading()

    const req = new Request(`${baseUrl}${endpoint}${id}`, {
      headers: getHeaders()
    })

    return fromFetch(req).pipe(
      switchMap((res) => res.text()),
      map((body) => {
        if (!body.startsWith('{')) {
          throw new Error(body)
        }
        return JSON.parse(body)
      }),
      finalize(() => !options.ignoreLoadingState && decrLoading())
    )
  },

  remove$: (id = '', options = {}) => {
    !options.ignoreLoadingState && incrLoading()

    const req = new Request(`${baseUrl}${endpoint}${id}`, {
      headers: getHeaders(),
      method: 'DELETE'
    })

    return fromFetch(req).pipe(
      switchMap((res) => res.text()),
      finalize(() => !options.ignoreLoadingState && decrLoading())
    )
  },

  subscribe: (subs = []) => {
    const _id = generateID()

    subscribers = [...subscribers, ...subs.map((s) => ({ ...s, _id }))]

    return {
      next: (type, data) => ws.current.send(JSON.stringify({ type, data })),
      unsubscribe: () => {
        subscribers = subscribers.filter((s) => s._id !== _id)
      }
    }
  }
})

export default adapter
